/**
 * The review strip's words (src/components/ReviewStrip.tsx, /feed?bench=1 for an admin), kept
 * pure so they are tested without a browser.
 *
 * The bench is emptied on a phone: the founder opens it as a feed, plays each card, reads who
 * made it and what the fit check said, and taps Live or Hide. Live and Hide call card_set_status,
 * the one door to live (migration 0020), whose trigger logs the flip with via='rpc', this actor
 * and the reason written here. The reasons are fixed words so the log and the maker's
 * "Your cards" read the same way: the maker of a hidden card sees "hidden: <word>".
 */
import type { BenchSummary } from '../api/stats';

/** What a Live tap records: a person touched it, it fit, and it is fit to show strangers. */
export const LIVE_REASON = 'reviewed: touch=y fit=y show=y';

/** The one-word reasons Hide asks for. Nothing else is accepted, so the log stays countable. */
export const HIDE_REASONS = ['does-not-fit', 'blank', 'broken', 'not-for-strangers', 'takedown'] as const;
export type HideReason = (typeof HIDE_REASONS)[number];

export function hideReason(word: HideReason): string {
  return `hidden: ${word}`;
}

/** Who made it and what the fit check said: '@mira · made with Claude · fit: wider than a phone'. */
export function benchWho(c: { handle: string | null; madeWith: string | null; fitSummary: string | null }): string {
  return [
    c.handle ? `@${c.handle}` : 'no handle',
    ...(c.madeWith ? [`made with ${c.madeWith}`] : []),
    `fit: ${c.fitSummary ?? 'not checked yet'}`,
  ].join(' · ');
}

/**
 * The bench at a glance, from bench_summary(): 'live 11 · bench 4 · oldest 6 h'. Without the
 * summary (no backend, or it failed) the strip still says how many this page has left to review.
 */
export function benchCountLine(b: BenchSummary | null, left: number): string {
  if (!b) return `${left} left to review`;
  const oldest = b.oldest_bench_age_h == null ? '' : ` · oldest ${Math.round(b.oldest_bench_age_h)} h`;
  return `live ${b.live} · bench ${b.bench}${oldest}`;
}

/** What the strip says about a card this page has already flipped. */
export function doneLine(outcome: { status: 'live' } | { status: 'hidden'; word: HideReason }): string {
  return outcome.status === 'live' ? 'Live. BLITZ for the next.' : `Hidden: ${outcome.word}. BLITZ for the next.`;
}
